import { useState } from 'react';
import { Box, Button, Checkbox, CircularProgress, Link, Paper, Stack, Typography } from '@mui/material';
import { Link as RouterLink, Navigate, useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import MainLayout from '../components/layout/MainLayout';
import AppHeader, { defaultUserAvatar } from '../components/layout/AppHeader';
import { sendOtp } from '../services/authService';
import { showAppError } from '../services/messageDialogService';
import { showAuthError } from '../utils/showAuthError';
import { isAuthenticated, setAuthSession } from '../storage/authStorage';
import { BAZZAR_PATH, PRIVACY_PATH, TERMS_PATH } from '../routes/paths';

const DEFAULT_COUNTRY_ISO = 'IN';
const DEFAULT_DIAL_CODE = '91';

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [loginMethod, setLoginMethod] = useState('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [countryIso, setCountryIso] = useState(DEFAULT_COUNTRY_ISO);
  const [dialCode, setDialCode] = useState(DEFAULT_DIAL_CODE);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleGetOtp = async () => {
    const localPhone = phoneNumber.replace(/\D/g, '').slice(0, 10);
    const localEmail = email.trim().toLowerCase();
    const isEmailMethod = loginMethod === 'email';

    if (isEmailMethod) {
      if (!localEmail) {
        showAppError('Please enter your email address.');
        return;
      }

      if (!isValidEmail(localEmail)) {
        showAppError('Please enter a valid email address.');
        return;
      }
    } else {
      if (!localPhone) {
        showAppError('Please enter your mobile number.');
        return;
      }

      if (localPhone.length !== 10) {
        showAppError('Please enter a valid 10-digit mobile number.');
        return;
      }
    }

    if (!termsAccepted) {
      showAppError('Please accept the Terms & Conditions and Privacy Policy.');
      return;
    }

    setLoading(true);

    try {
      await sendOtp({
        method: loginMethod,
        phone: isEmailMethod ? '' : localPhone,
        email: isEmailMethod ? localEmail : '',
        countryCode: dialCode,
        countryIso,
      });

      setAuthSession({
        method: loginMethod,
        phone: isEmailMethod ? '' : localPhone,
        email: isEmailMethod ? localEmail : '',
        countryCode: dialCode,
        countryIso,
      });

      navigate('/verify', {
        state: {
          method: loginMethod,
          phone: isEmailMethod ? '' : localPhone,
          email: isEmailMethod ? localEmail : '',
          identifier: isEmailMethod ? localEmail : localPhone,
          countryCode: dialCode,
          countryIso,
        },
      });
    } catch (error) {
      showAuthError(error, 'Unable to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (isAuthenticated()) {
    return <Navigate to={BAZZAR_PATH} replace />;
  }

  return (
    <MainLayout>
      <AppHeader showSubtitle endVariant="profile" avatarSrc={defaultUserAvatar} />
      <Box
        sx={{
          minHeight: { xs: '68vh', md: '72vh' },
          display: 'grid',
          placeItems: 'center',
          py: { xs: 2, md: 5 },
          px: { xs: 1.25, sm: 2 },
          width: '100%',
          boxSizing: 'border-box',
          overflowX: 'hidden',
        }}
      >
        <Paper
          sx={{
            display: 'flex',
            width: '100%',
            maxWidth: { xs: '100%', sm: 480 },
            minHeight: { xs: 'auto', sm: '443px' },
            pt: { xs: 3, sm: '48px' },
            px: { xs: 1.5, sm: 3 },
            pb: 3,
            flexDirection: 'column',
            alignItems: 'center',
            gap: { xs: 2.5, sm: '38px' },
            borderRadius: { xs: '24px', sm: '36px' },
            bgcolor: 'rgba(0, 0, 0, 0.50)',
            boxSizing: 'border-box',
          }}
        >
          <Stack spacing={{ xs: 2, sm: 2.5 }} sx={{ width: '100%', maxWidth: 400 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: '#fff',
                textAlign: 'center',
                fontSize: { xs: 20, sm: 24, md: 28 },
                letterSpacing: { xs: '1px', sm: '2.5px' },
                textTransform: 'uppercase',
                lineHeight: 1.2,
                px: { xs: 0.5, sm: 0 },
              }}
            >
              LOGIN TO CONTINUE
            </Typography>

            <Stack spacing={1.3}>
              <Box
                sx={{
                  width: '100%',
                  height: 50,
                  borderRadius: '60px',
                  border: '1px solid #C0F',
                  boxShadow: '0 2px 12px rgba(236, 29, 255, 0.36)',
                  bgcolor: '#5813B3',
                  p: '4px',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 4,
                    bottom: 4,
                    left: loginMethod === 'phone' ? 4 : 'calc(50% - 2px)',
                    width: 'calc(50% - 4px)',
                    borderRadius: '100px',
                    border: '1px solid #FFC5F7',
                    background: 'linear-gradient(90deg, #822AFF 0%, #FF00F2 100%)',
                    boxShadow: 'inset 2px 2px 5px rgba(255,255,255,0.35), inset -2px -2px 6px rgba(0,0,0,0.25)',
                    transition: 'left 180ms ease',
                  }}
                />
                <Button
                  onClick={() => setLoginMethod('phone')}
                  sx={{
                    flex: 1,
                    zIndex: 1,
                    minWidth: 0,
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 500,
                    textTransform: 'none',
                    letterSpacing: '0.5px',
                  }}
                >
                  Mobile
                </Button>
                <Button
                  onClick={() => setLoginMethod('email')}
                  sx={{
                    flex: 1,
                    zIndex: 1,
                    minWidth: 0,
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 500,
                    textTransform: 'none',
                    letterSpacing: '0.5px',
                  }}
                >
                  Email
                </Button>
              </Box>

              <Box
                sx={{
                  width: '100%',
                  height: '50px',
                  borderRadius: '100px',
                  border: '1px solid #D43CCA',
                  background: 'rgba(0, 0, 0, 0.66)',
                  display: 'flex',
                  alignItems: 'center',
                  overflow: 'hidden',
                  ...(loginMethod === 'phone' && {
                    '& .react-tel-input': {
                      width: 'auto',
                      position: 'static',
                      flexShrink: 0,
                    },
                    '& .react-tel-input .form-control': {
                      display: 'none',
                    },
                    '& .react-tel-input .flag-dropdown': {
                      position: 'static',
                      border: 'none',
                      background: 'transparent',
                    },
                    '& .react-tel-input .selected-flag': {
                      position: 'static',
                      width: '32px',
                      paddingLeft: '10px',
                      background: 'transparent !important',
                    },
                    '& .react-tel-input .selected-flag .arrow': {
                      display: 'none',
                    },
                    '& .react-tel-input .country-list': {
                      background: '#1A0018',
                      color: '#fff',
                      border: '1px solid #822AFF',
                      zIndex: 10,
                    },
                    '& .react-tel-input .country-list .country:hover': {
                      background: '#341167',
                    },
                    '& .react-tel-input .country-list .country.highlight': {
                      background: '#4E1999',
                    },
                    '& .react-tel-input .selected-flag .flag': {
                      transform: 'scale(0.92)',
                      position: 'initial',
                    },
                  }),
                }}
              >
                {loginMethod === 'phone' ? (
                  <>
                    <PhoneInput
                      country={countryIso.toLowerCase()}
                      value=""
                      onChange={(_, countryData) => {
                        setCountryIso(countryData?.countryCode?.toUpperCase() || DEFAULT_COUNTRY_ISO);
                        setDialCode(countryData?.dialCode || DEFAULT_DIAL_CODE);
                      }}
                      enableSearch
                      disableCountryCode
                      disableCountryGuess
                      specialLabel=""
                      inputProps={{ readOnly: true, tabIndex: -1 }}
                    />
                    <Typography
                      sx={{
                        color: '#E6D4FF',
                        fontSize: 16,
                        fontWeight: 600,
                        letterSpacing: '0.8px',
                        userSelect: 'none',
                        flexShrink: 0,
                        textTransform: 'uppercase',
                      }}
                    >
                      {countryIso}
                    </Typography>
                    <Box sx={{ width: '1px', height: 24, bgcolor: '#E6D4FF80', mx: 1.4, flexShrink: 0 }} />
                    <Box
                      component="input"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel"
                      name="phone"
                      value={phoneNumber}
                      onChange={(event) => {
                        const digits = event.target.value.replace(/\D/g, '');
                        setPhoneNumber(digits.slice(0, 10));
                      }}
                      placeholder="Enter mobile number"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        color: '#E6D4FF',
                        pr: 2,
                        fontSize: 16,
                        fontWeight: 600,
                        letterSpacing: '0.8px',
                        lineHeight: '24px',
                        '&::placeholder': {
                          color: '#E6D4FF',
                          opacity: 1,
                        },
                      }}
                    />
                  </>
                ) : (
                  <Box
                    component="input"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    name="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="Enter Email"
                    sx={{
                      width: '100%',
                      border: 'none',
                      outline: 'none',
                      background: 'transparent',
                      color: '#E6D4FF',
                      px: 2,
                      fontSize: 16,
                      fontWeight: 600,
                      letterSpacing: '0.8px',
                      lineHeight: '24px',
                      '&::placeholder': {
                        color: '#E6D4FF',
                        opacity: 1,
                      },
                    }}
                  />
                )}
              </Box>
            </Stack>

            <Button
              onClick={handleGetOtp}
              disabled={loading}
              sx={{
                mt: 0.5,
                height: 50,
                width: '100%',
                borderRadius: '100px',
                border: '1px solid #FFF',
                color: '#fff',
                background: 'linear-gradient(90deg, #822AFF 0%, #FF00F2 100%)',
                boxShadow: '0 0 20px rgba(252, 0, 241, 0.36)',
                fontSize: { xs: 16, sm: 18 },
                fontWeight: 500,
                textTransform: 'uppercase',
                '&:hover': {
                  background: 'linear-gradient(90deg, #822AFF 0%, #FF00F2 100%)',
                },
                '&.Mui-disabled': {
                  color: '#fff',
                  opacity: 0.7,
                },
              }}
            >
              {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'GET OTP'}
            </Button>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: 1, px: { xs: 0.25, sm: 0 } }}>
              <Checkbox
                size="small"
                checked={termsAccepted}
                onChange={(event) => setTermsAccepted(event.target.checked)}
                sx={{
                  p: 0.4,
                  color: '#fff',
                  flexShrink: 0,
                  '&.Mui-checked': { color: '#fff' },
                }}
              />
              <Typography sx={{ fontSize: 12, color: '#fff', lineHeight: '20px', wordBreak: 'break-word' }}>
                I agree to the{' '}
                <Link component={RouterLink} to={TERMS_PATH} underline="always" sx={{ color: '#FF00F2', fontSize: 12 }}>
                  Terms & Conditions
                </Link>{' '}
                and{' '}
                <Link component={RouterLink} to={PRIVACY_PATH} underline="always" sx={{ color: '#FF00F2', fontSize: 12 }}>
                  Privacy Policy
                </Link>
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Box>
    </MainLayout>
  );
}
